/* eslint-disable i18next/no-literal-string */
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import cx from 'classnames';
import type {WireframeReportPillar, WireframeReportResponse} from 'client';

import {HeadcountBreakdownTable} from './HeadcountBreakdownTable';
import {CategoryDonut, CategoryStackedBars, CategorySwatch, RankedBars} from './WireframeCharts';
import styles from './WireframeReport.module.css';
import {
  formatHeadcount,
  formatShare,
  formatUsd,
  shareOf,
  toNumber,
  WIREFRAME_CATEGORIES,
} from './wireframeReportFormat';
import {WireframeSection} from './WireframeSection';

const TOP_WORKSTREAMS_LIMIT = 10;

const PillarCard = ({
  pillar,
  hrefForWorkstream,
}: {
  pillar: WireframeReportPillar;
  hrefForWorkstream: (id: number) => string;
}) => {
  const rowsById = new Map(pillar.rows.map((row) => [row.workstream_id, row]));
  const top = pillar.top_workstream_ids.slice(0, TOP_WORKSTREAMS_LIMIT).flatMap((id) => {
    const row = rowsById.get(id);
    return row ? [row] : [];
  });
  const total = toNumber(pillar.headcount.headcount_fte);
  return (
    <WireframeSection
      title={pillar.name}
      subtitle={`Top ${TOP_WORKSTREAMS_LIMIT} workstreams by headcount`}
      aside={
        <Text size="small" isBold className={styles.nowrap}>
          {formatHeadcount(total)} HC · {formatUsd(pillar.spend.total_usd)}
        </Text>
      }
    >
      <Stack gap="24">
        {top.length === 0 ? (
          <Text size="small" color="subtle">
            No workstream headcount entered yet.
          </Text>
        ) : (
          <RankedBars
            ariaLabel={`${pillar.name} top workstreams by headcount`}
            items={top.map((row) => ({
              key: row.workstream_id,
              label: row.name,
              href: hrefForWorkstream(row.workstream_id),
              value: toNumber(row.headcount.headcount_fte),
              detail: (
                <Text tagName="div" size="small" color="inherit">
                  {formatHeadcount(row.headcount.headcount_fte)} HC ({formatShare(row.pillar_share)}{' '}
                  of pillar) · {formatUsd(row.spend.total_usd)}
                </Text>
              ),
            }))}
          />
        )}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          paddingTop="16"
          columnGap="24"
          rowGap="24"
          className={styles.cardDivider}
        >
          <CategoryDonut headcount={pillar.headcount} spend={pillar.spend} title={pillar.name} />
          <dl className={cx(atoms({margin: '0'}), styles.categoryList)}>
            <Stack gap="12">
              {WIREFRAME_CATEGORIES.map((category) => {
                const value = toNumber(pillar.headcount[category.headcount]);
                return (
                  <div key={category.key}>
                    <dt className={atoms({display: 'flex', alignItems: 'center', columnGap: '10'})}>
                      <CategorySwatch category={category} />
                      <Text size="small" isBold>
                        {category.label}
                      </Text>
                      <Text size="small" color="subtle">
                        {formatShare(shareOf(value, total))}
                      </Text>
                    </dt>
                    <dd
                      className={atoms({
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '0',
                        // Under the label: past the 14px swatch and its 10px gap.
                        paddingLeft: '24',
                        paddingTop: '4',
                        columnGap: '16',
                      })}
                    >
                      <Text size="small" color="subtle">
                        {formatHeadcount(value)} HC
                      </Text>
                      <Text size="small">{formatUsd(pillar.spend[category.spend])}</Text>
                    </dd>
                  </div>
                );
              })}
            </Stack>
          </dl>
        </Box>
      </Stack>
    </WireframeSection>
  );
};

/** The whole half: headcount and spend by org and by pillar, their category mix, and each pillar's largest workstreams. */
export const WireframeCompanyView = ({
  report,
  timeframeLabel,
  hrefForWorkstream,
}: {
  report: WireframeReportResponse;
  timeframeLabel: string;
  hrefForWorkstream: (id: number) => string;
}) => {
  const total = {headcount: report.headcount, spend: report.spend};
  const timeframeAside = (
    <Text size="small" isBold className={styles.nowrap}>
      {timeframeLabel}
    </Text>
  );
  // Unassigned alone means the half has no orgs to break down by.
  const hasOrgs = report.orgs.some((org) => org.org_id !== null);
  return (
    <Stack gap="24">
      {hasOrgs && (
        <WireframeSection
          title="Headcount by org"
          subtitle="By Cost Location"
          aside={timeframeAside}
        >
          <HeadcountBreakdownTable
            label="Org"
            rows={report.orgs.map((org) => ({
              key: org.org_id ?? 'unassigned',
              name: org.name,
              headcount: org.headcount,
              spend: org.spend,
              companyShare: org.company_share,
              isMuted: org.org_id === null,
            }))}
            total={total}
          />
        </WireframeSection>
      )}
      <WireframeSection
        title="Headcount by pillar"
        subtitle="By Cost Location"
        aside={timeframeAside}
      >
        <HeadcountBreakdownTable
          label="Pillar"
          rows={report.pillars.map((pillar) => ({
            key: pillar.pillar_workstream_id,
            name: pillar.name,
            headcount: pillar.headcount,
            spend: pillar.spend,
            companyShare: pillar.company_share,
          }))}
          total={total}
        />
      </WireframeSection>
      <WireframeSection title="By cost category">
        <CategoryStackedBars
          rows={[
            ...report.pillars.map((pillar) => ({
              key: pillar.pillar_workstream_id,
              label: pillar.name,
              headcount: pillar.headcount,
              spend: pillar.spend,
            })),
            {
              key: 'company',
              label: 'Company total',
              headcount: report.headcount,
              spend: report.spend,
              emphasis: true,
            },
          ]}
        />
      </WireframeSection>
      <div className={styles.cardGrid}>
        {report.pillars.map((pillar) => (
          <PillarCard
            key={pillar.pillar_workstream_id}
            pillar={pillar}
            hrefForWorkstream={hrefForWorkstream}
          />
        ))}
      </div>
    </Stack>
  );
};
